import axios from 'axios';
import jsPDF from 'jspdf';
import { END_POINT, getJwtToken } from '../../Redux/AdminReducer/action';

/**
 * Purchase-order and return-order PDFs.
 *
 * One PDF per id: the header on top and every medicine line of that order in
 * the table below. Nothing in the inventory flow is emailed to the distributor
 * any more, so these PDFs are the only copy of an order that leaves the app.
 */

const MARGIN = 14;
const PAGE_BOTTOM = 276;

const PURCHASE_COLUMNS = [
  { key: 'index', label: '#', width: 8 },
  { key: 'nameOfMedicine', label: 'Medicine', width: 36 },
  { key: 'company', label: 'Company', width: 28 },
  { key: 'potencyOrPower', label: 'Potency', width: 20 },
  { key: 'typeOfMedicine', label: 'Type', width: 18 },
  { key: 'packing', label: 'Packing', width: 20 },
  { key: 'distributorName', label: 'Distributor', width: 30 },
  { key: 'expiryDate', label: 'Expiry', width: 20 },
  { key: 'quantity', label: 'Qty', width: 14, align: 'right' },
];

const RETURN_COLUMNS = [
  { key: 'index', label: '#', width: 8 },
  { key: 'order', label: 'Order Id', width: 26 },
  { key: 'nameOfMedicine', label: 'Medicine', width: 38 },
  { key: 'company', label: 'Company', width: 24 },
  { key: 'potencyOrPower', label: 'Potency', width: 18 },
  { key: 'quantity', label: 'Ordered', width: 20, align: 'right' },
  { key: 'damage', label: 'Damaged', width: 22, align: 'right' },
  { key: 'reason', label: 'Reason', width: 26 },
];

const authHeaders = () => ({ headers: { Authorization: getJwtToken() } });

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const sumBy = (items, key) => items.reduce((sum, item) => sum + (Number(item?.[key]) || 0), 0);

const cellValue = (item, column, index) => {
  if (column.key === 'index') return String(index + 1);
  if (column.key === 'distributorName') return String(item?.distributorName || item?.distributor || '-');
  if (column.key === 'expiryDate') return formatDate(item?.expiryDate);
  const value = item?.[column.key];
  return value === undefined || value === null || value === '' ? '-' : String(value);
};

// The clinic letterhead is the same for every PDF in a session, so it is fetched once.
let clinicPromise = null;
export const fetchClinicHeader = () => {
  if (!clinicPromise) {
    clinicPromise = axios
      .get(`${END_POINT}/clinic-profile`, authHeaders())
      .then(({ data }) => data?.data || {})
      .catch(() => ({}));
  }
  return clinicPromise;
};

const drawTableHeader = (doc, columns, y) => {
  doc.setFillColor(241, 245, 249);
  doc.rect(MARGIN, y, columns.reduce((sum, c) => sum + c.width, 0), 8, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(30, 41, 59);

  let x = MARGIN;
  columns.forEach((column) => {
    const textX = column.align === 'right' ? x + column.width - 2 : x + 2;
    doc.text(column.label, textX, y + 5.4, { align: column.align === 'right' ? 'right' : 'left' });
    x += column.width;
  });

  return y + 8;
};

/**
 * Shared renderer for both document types.
 *
 * @param {object} spec    { title, columns, meta: [[label, value], ...], totals: [left, right] }
 * @param {Array}  items   medicine lines
 * @param {object} clinic  clinic profile used as the letterhead
 */
const buildPdf = (spec, items, clinic = {}) => {
  const doc = new jsPDF('p', 'mm', 'a4');
  const columns = spec.columns;
  const contentWidth = columns.reduce((sum, c) => sum + c.width, 0);

  // Letterhead
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(15, 23, 42);
  doc.text(clinic?.ClinicName || 'Clinic', 105, 18, { align: 'center' });

  const contact = [clinic?.DoctorName, clinic?.ClinicAddress, clinic?.ClinicContact, clinic?.ClinicEmail]
    .filter(Boolean)
    .join('  |  ');
  if (contact) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(contact, 105, 24, { align: 'center' });
  }

  doc.setDrawColor(203, 213, 225);
  doc.line(MARGIN, 28, MARGIN + contentWidth, 28);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(15, 23, 42);
  doc.text(spec.title, 105, 36, { align: 'center' });

  // Meta block, two columns
  doc.setFontSize(10);
  doc.setTextColor(71, 85, 105);

  const metaY = 45;
  spec.meta.forEach(([label, value], index) => {
    const isRight = index % 2 === 1;
    const x = isRight ? 115 : MARGIN;
    const rowY = metaY + Math.floor(index / 2) * 6;
    doc.setFont('helvetica', 'bold');
    doc.text(`${label}:`, x, rowY);
    doc.setFont('helvetica', 'normal');
    doc.text(doc.splitTextToSize(String(value), isRight ? 55 : 65), x + 24, rowY);
  });

  let y = drawTableHeader(doc, columns, metaY + Math.ceil(spec.meta.length / 2) * 6 + 8);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);

  items.forEach((item, index) => {
    const cells = columns.map((column) => ({
      column,
      lines: doc.splitTextToSize(cellValue(item, column, index), column.width - 4),
    }));
    const rowHeight = Math.max(7, ...cells.map((cell) => cell.lines.length * 4.6 + 2.4));

    if (y + rowHeight > PAGE_BOTTOM) {
      doc.addPage();
      y = drawTableHeader(doc, columns, 20);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
    }

    let x = MARGIN;
    doc.setTextColor(30, 41, 59);
    cells.forEach(({ column, lines }) => {
      const textX = column.align === 'right' ? x + column.width - 2 : x + 2;
      doc.text(lines, textX, y + 5, { align: column.align === 'right' ? 'right' : 'left' });
      x += column.width;
    });

    doc.setDrawColor(226, 232, 240);
    doc.line(MARGIN, y + rowHeight, MARGIN + contentWidth, y + rowHeight);
    y += rowHeight;
  });

  // Totals
  if (y + 12 > PAGE_BOTTOM) {
    doc.addPage();
    y = 20;
  }
  doc.setFillColor(248, 250, 252);
  doc.rect(MARGIN, y, contentWidth, 9, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.setTextColor(15, 23, 42);
  doc.text(spec.totals[0], MARGIN + 2, y + 6);
  doc.text(spec.totals[1], MARGIN + contentWidth - 2, y + 6, { align: 'right' });
  y += 9;

  // Footer
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(100, 116, 139);
  doc.text(`Generated on ${formatDate(new Date())}`, MARGIN, y + 10);
  doc.text('Authorised Signature', MARGIN + contentWidth, y + 22, { align: 'right' });
  doc.setDrawColor(148, 163, 184);
  doc.line(MARGIN + contentWidth - 50, y + 18, MARGIN + contentWidth, y + 18);

  return doc;
};

export const buildOrderPdf = (order, clinic = {}) => {
  const items = Array.isArray(order?.items) ? order.items : [];
  const distributors = [...new Set(items.map((item) => item?.distributorName || item?.distributor).filter(Boolean))];

  return buildPdf(
    {
      title: 'PURCHASE ORDER',
      columns: PURCHASE_COLUMNS,
      meta: [
        ['Order ID', order?.Order_Id || '-'],
        ['Status', order?.status === 'received' ? 'Received' : 'Pending'],
        ['Order Date', formatDate(order?.date)],
        ['Distributor', distributors.join(', ') || '-'],
      ],
      totals: [`Total medicines: ${items.length}`, `Total quantity: ${sumBy(items, 'quantity')}`],
    },
    items,
    clinic
  );
};

export const buildReturnPdf = (returnOrder, clinic = {}) => {
  const items = Array.isArray(returnOrder?.items) ? returnOrder.items : [];
  const distributors = [...new Set(items.map((item) => item?.distributorName || item?.distributor).filter(Boolean))];
  const orders = [...new Set(items.map((item) => item?.order).filter(Boolean))];

  return buildPdf(
    {
      title: 'RETURN ORDER',
      columns: RETURN_COLUMNS,
      meta: [
        ['Return ID', returnOrder?.Return_Id || '-'],
        ['Against Order', orders.join(', ') || '-'],
        ['Return Date', formatDate(returnOrder?.date)],
        ['Distributor', distributors.join(', ') || '-'],
      ],
      totals: [`Total medicines: ${items.length}`, `Total damaged: ${sumBy(items, 'damage')}`],
    },
    items,
    clinic
  );
};

/** Build and save the PDF for an order that is already in hand. */
export const downloadOrderPdf = async (order) => {
  const clinic = await fetchClinicHeader();
  buildOrderPdf(order, clinic).save(`${order?.Order_Id || 'purchase-order'}.pdf`);
};

/** Same, but pulls every line of the order from the server first. */
export const downloadOrderPdfById = async (orderId) => {
  const { data } = await axios.get(
    `${END_POINT}/inventory/orders/by-order-id/${encodeURIComponent(orderId)}`,
    authHeaders()
  );
  await downloadOrderPdf(data);
};

export const downloadReturnPdf = async (returnOrder) => {
  const clinic = await fetchClinicHeader();
  buildReturnPdf(returnOrder, clinic).save(`${returnOrder?.Return_Id || 'return-order'}.pdf`);
};

export const downloadReturnPdfById = async (returnId) => {
  const { data } = await axios.get(
    `${END_POINT}/inventory/returns/by-return-id/${encodeURIComponent(returnId)}`,
    authHeaders()
  );
  await downloadReturnPdf(data);
};
